import React from 'react';
import {
    Box,
    Typography,
    Button,
    Grid,
    Chip,
    Paper,
    Stack,
    Avatar,
    AvatarGroup
} from '@mui/material';
import { 
    ArrowRight, 
    PlayCircle, 
    TrendingUp, 
    FileText, 
    CreditCard,
    CheckCircle2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Heronew = () => {
    const navigate = useNavigate();

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: '100vw',
                minHeight: '100vh',
                overflowX: 'hidden', // Prevents horizontal scroll on mobile
                boxSizing: 'border-box',
                backgroundColor: '#F8FAFC', 
                backgroundImage: 'radial-gradient(at 100% 0%, rgba(79, 70, 229, 0.08) 0px, transparent 50%), radial-gradient(at 0% 100%, rgba(16, 185, 129, 0.05) 0px, transparent 50%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                // Responsive padding: larger on mobile to account for stacked layout
                pt: { xs: 10, sm: 12, md: 15 },
                pb: { xs: 8, sm: 10, md: 10 },
            }}
        >
            <Box sx={{ maxWidth: '1280px', width: '100%', mx: 'auto', px: { xs: 2, sm: 4, md: 6 } }}>
                {/* 
                  Grid reverses direction on mobile so text is always on top, 
                  but remains side-by-side on desktop 
                */}
                <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
                    
                    {/* Left Column: Text & CTAs */}
                    <Grid item xs={12} md={6}>
                        <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                            <Chip
                                label="Trusted by 100+ businesses"
                                icon={<Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#10B981', ml: 1 }} />}
                                sx={{
                                    mb: { xs: 2, md: 3 },
                                    backgroundColor: '#EFF6FF',
                                    color: '#1D4ED8',
                                    fontWeight: 600,
                                    borderRadius: '8px',
                                    border: '1px solid #BFDBFE',
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    '& .MuiChip-label': { px: 2 }
                                }}
                            />

                            {/* Responsive Typography scaling */}
                            <Typography
                                variant="h1"
                                sx={{
                                    fontWeight: 800,
                                    fontSize: { xs: '2.25rem', sm: '3rem', md: '3.5rem', lg: '4rem' },
                                    lineHeight: 1.1,
                                    letterSpacing: '-0.03em',
                                    color: '#0F172A',
                                    mb: 3,
                                }}
                            >
                                Smarter accounting for <Box component="span" sx={{ color: '#4F46E5', display: 'inline-block' }}>growing</Box> teams.
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: { xs: '1rem', sm: '1.125rem' },
                                    lineHeight: 1.7,
                                    color: '#475569',
                                    mb: { xs: 4, md: 5 },
                                    maxWidth: '540px',
                                    mx: { xs: 'auto', md: 0 }
                                }}
                            >
                                Say goodbye to manual data entry. Automate your invoicing, track expenses in real-time, and get paid faster all from one unified dashboard.
                            </Typography>

                            {/* Buttons stack vertically on tiny screens, horizontally on small+ */}
                            <Stack 
                                direction={{ xs: 'column', sm: 'row' }} 
                                spacing={2} 
                                justifyContent={{ xs: 'center', md: 'flex-start' }}
                                sx={{ mb: { xs: 5, md: 6 } }}
                            >
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={() => navigate("/register")}
                                    endIcon={<ArrowRight size={20} />}
                                    fullWidth={{ xs: true, sm: false }} // Full width only on mobile phones
                                    sx={{
                                        backgroundColor: '#4F46E5',
                                        color: 'white',
                                        fontWeight: 600,
                                        px: 4,
                                        py: 1.5,
                                        borderRadius: '12px',
                                        boxShadow: '0 10px 25px -5px rgba(79, 70, 229, 0.4)',
                                        textTransform: 'none',
                                        fontSize: '1rem',
                                        '&:hover': { backgroundColor: '#4338CA', boxShadow: '0 10px 25px -5px rgba(79, 70, 229, 0.6)' }
                                    }}
                                >
                                    Start Free Trial
                                </Button>
                                <Button
                                href="/plan-and-pricing"
                                    variant="outlined"
                                    size="large"
                                    fullWidth={{ xs: true, sm: false }}
                                    sx={{
                                        color: '#0F172A',
                                        borderColor: '#CBD5E1',
                                        fontWeight: 600,
                                        px: 4,
                                        py: 1.5,
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontSize: '1rem',
                                        '&:hover': { backgroundColor: '#F1F5F9', borderColor: '#94A3B8' }
                                    }}
                                >
                                    Pricing
                                </Button>
                            </Stack>

                            {/* Social Proof */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', md: 'flex-start' }, gap: 2 }}>
                                {/**<AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 32, height: 32, border: '2px solid #F8FAFC' } }}>
                                    <Avatar alt="User 1" src="https://i.pravatar.cc/100?img=1" />
                                    <Avatar alt="User 2" src="https://i.pravatar.cc/100?img=2" />
                                    <Avatar alt="User 3" src="https://i.pravatar.cc/100?img=3" />
                                    <Avatar alt="User 4" src="https://i.pravatar.cc/100?img=4" />
                                </AvatarGroup>*/}
                                <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, color: '#64748B', fontWeight: 500 }}>
                                    v26.07
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>

                    {/* Right Column: Abstract Dashboard / Floating Cards */}
                    <Grid item xs={12} md={6}>
                        <Box sx={{ 
                            position: 'relative', 
                            height: { xs: '380px', sm: '450px', md: '550px' }, 
                            width: '100%',
                            // Slightly scales down the entire visual container on mobile to prevent clipping
                            transform: { xs: 'scale(0.9)', sm: 'scale(1)' },
                            transformOrigin: 'center center'
                        }}>
                            {/* Abstract Background Shape */}
                            <Box sx={{
                                position: 'absolute',
                                top: '5%',
                                right: '5%',
                                width: { xs: '90%', md: '80%' },
                                height: { xs: '90%', md: '80%' },
                                background: 'linear-gradient(135deg, #E0E7FF 0%, #F3E8FF 100%)',
                                borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
                                transform: 'rotate(-5deg)',
                                zIndex: 0
                            }} />

                            {/* Floating Card 1: Revenue (Top Right) */}
                            <Paper sx={{
                                position: 'absolute',
                                top: { xs: '0%', md: '10%' },
                                right: { xs: '0%', sm: '5%', md: '10%' },
                                width: { xs: '220px', sm: '260px', md: '280px' },
                                p: { xs: 2, md: 3 },
                                borderRadius: '20px',
                                boxShadow: '0 20px 40px -10px rgba(0,0,0,0.08)',
                                zIndex: 2,
                                bgcolor: 'white'
                            }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Box sx={{ p: 1, bgcolor: '#F0FDF4', borderRadius: '12px', color: '#16A34A', display: 'flex' }}>
                                        <TrendingUp size={20} />
                                    </Box>
                                    <Typography sx={{ color: '#16A34A', fontWeight: 600, fontSize: '0.875rem' }}>+14.5%</Typography>
                                </Stack>
                                <Typography sx={{ color: '#64748B', fontSize: '0.875rem', mb: 0.5 }}>Total Revenue</Typography>
                                <Typography sx={{ color: '#0F172A', fontSize: { xs: '1.5rem', md: '1.75rem' }, fontWeight: 800 }}>LKR.12,500.00</Typography>
                            </Paper>

                            {/* Floating Card 2: Invoice Sent (Middle Left) */}
                            <Paper sx={{
                                position: 'absolute',
                                top: { xs: '40%', md: '45%' },
                                left: { xs: '0%', sm: '5%', md: '-5%' },
                                width: { xs: '240px', sm: '260px' },
                                p: { xs: 2, md: 2.5 },
                                borderRadius: '20px',
                                boxShadow: '0 20px 40px -10px rgba(0,0,0,0.12)',
                                zIndex: 3,
                                bgcolor: 'white'
                            }}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Box sx={{ p: 1, bgcolor: '#EFF6FF', borderRadius: '12px', color: '#2563EB', display: 'flex' }}>
                                        <FileText size={20} />
                                    </Box>
                                    <Box>
                                        <Typography sx={{ color: '#0F172A', fontWeight: 700, fontSize: '0.95rem' }}>Invoice #402</Typography>
                                        <Typography sx={{ color: '#64748B', fontSize: '0.8rem' }}>Sent to Acme Corp</Typography>
                                    </Box>
                                    <Box sx={{ ml: 'auto !important', color: '#10B981', display: 'flex' }}>
                                        <CheckCircle2 size={20} />
                                    </Box>
                                </Stack>
                            </Paper>

                            {/* Floating Card 3: Expense (Bottom Right) */}
                            <Paper sx={{
                                position: 'absolute',
                                bottom: { xs: '5%', md: '10%' },
                                right: { xs: '5%', md: '15%' },
                                width: { xs: '200px', sm: '220px', md: '240px' },
                                p: { xs: 1.5, md: 2.5 },
                                borderRadius: '20px',
                                boxShadow: '0 20px 40px -10px rgba(0,0,0,0.08)',
                                zIndex: 1,
                                bgcolor: 'white'
                            }}>
                                <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
                                    <Box sx={{ p: 1, bgcolor: '#FEF2F2', borderRadius: '12px', color: '#DC2626', display: 'flex' }}>
                                        <CreditCard size={18} />
                                    </Box>
                                    <Typography sx={{ color: '#0F172A', fontWeight: 700, fontSize: '0.9rem' }}>New Expense</Typography>
                                </Stack>
                                <Typography sx={{ color: '#64748B', fontSize: '0.875rem', display: 'flex', justifyContent: 'space-between' }}>
                                    <span>Tax</span>
                                    <span style={{ fontWeight: 600, color: '#0F172A' }}>-$240.00</span>
                                </Typography>
                            </Paper>

                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
};

export default Heronew;